import Vue from 'vue'
import VueSimpleSpinner from 'vue-simple-spinner'
import {UserRoleEnum} from '@/enums/userRolesEnum'
import {convertDateStringToUTCDate, convertDateToUTC} from '@/utils/dateUtil';
import {IssuableController} from "@/components/provider/cards/issuables/IssuableModels";
import {
  getExpirationNoticeDate,
  issuableExpired,
  issuableExpiring
} from "@/components/provider/cards/issuables/IssuableUtil";

export default Vue.component('issuable-card', {
  name: 'IssuableCard',
  props: {
    loadingSecondaryData: {
      type: Boolean,
      default: false
    },
    issuableController: {
      type: Object as () => IssuableController<any>
    },
    displayExpiredBadge: {
      type: Boolean,
      default: true
    }
  },
  data () {
    let emptyArray: Array<any> = [];
    return {
      cardExpanded: false,
      loadingInitialData: false,
      issuables: emptyArray,
      expiredIssuableExpanded: false,
    }
  },
  components: {
    VueSimpleSpinner,
  },
  computed: {
    isProviderUser (): boolean {
      return this.$store.getters['user/hasOnlyRole'](UserRoleEnum.PROVIDER);
    },
    isLoadingData(): boolean {
      return this.loadingInitialData || this.loadingSecondaryData;
    },
    haveExpiredIssuables(): boolean {
      return this.expiredCount > 0;
    },
    expiringCount(): number {
      const today = convertDateToUTC(new Date());
      return this.issuables.filter(issuable => issuableExpiring(issuable, today)).length;
    },
    expiredIssuables(): any[] {
      const today = convertDateToUTC(new Date());
      return this.issuables.filter(issuable => issuableExpired(issuable, today))
    },
    unexpiredIssuables(): any[] {
      const today = convertDateToUTC(new Date());
      return this.issuables.filter(issuable => !issuableExpired(issuable, today))
    },
    expiredCount(): number {
      return this.expiredIssuables.length;
    },
    shouldExpandExpiredItems(): boolean {
      return this.expiredIssuableExpanded || this.expiredIssuables.some(issuable => issuable.isEditing);
    }
  },
  created() {
    this.getIssuables();
  },
  methods: {
    unexpiredTasks() {
      return
    },
    async expandCard () {
      this.cardExpanded = !this.cardExpanded;
    },
    async expandExpiredCard () {
      this.expiredIssuableExpanded = !this.expiredIssuableExpanded;
    },
    preserveOpenItems (existingItems: any, newItems: any, uniqueId: string) {
      existingItems = existingItems.filter(function (obj: any) { return !obj.isNew; });

      // If the length of existingItems < newItems, it's probably an "Insert"
      if (existingItems.length && (existingItems.length < newItems.length)) {
        // Find the new item and set "isEditing" to true
        let newItem = newItems.filter(function (obj1: any) { return !existingItems.some(function (obj2: any) { return obj1[uniqueId] === obj2[uniqueId]; }); });
        newItem.forEach( function (obj: any) { obj.isEditing = true; } );
      } else {
        // If the length of existingItems === newItems, it's probably an "Update"
        existingItems.forEach(function (existingItem: any) {
          if (existingItem.isEditing) {
            newItems.forEach(function (newItem: any) {
              if (newItem[uniqueId] === existingItem[uniqueId]) {
                newItem.isEditing = existingItem.isEditing;
              }
            })
          }
        })
      }
      return newItems;
    },
    async getIssuables () {
      let self = this;
      let showLoadingSpinnerTimeout = setTimeout(function () {
        self.loadingInitialData = true;
      }, 0);
      try {
        const response = await this.issuableController.getAllForProvider(this.$route.params.providerId);
        clearTimeout(showLoadingSpinnerTimeout);
        this.loadingInitialData = false;
        if (!response.data || !response.data.data) {
          throw new Error(`Error retrieving ${this.issuableController.entityName}`);
        }
        let freshIssuables = response.data.data;
        freshIssuables.forEach( (issuable: any) => {
          issuable.id = issuable[self.issuableController.primaryIdField];
          issuable.isEditing = false;
          issuable.isNew = false;
          issuable.updatingData = false;
          if (issuable.expirationNoticeDate == null) {
            issuable.expirationNoticeSetting = 0;
          }
          issuable.issuedDate = convertDateStringToUTCDate(issuable.issuedDate);
          issuable.expirationDate = convertDateStringToUTCDate(issuable.expirationDate);
          if (issuable.expirationNoticeDate != null) {
            issuable.expirationNoticeDate = convertDateStringToUTCDate(issuable.expirationNoticeDate);
          }
          if (issuable.expirationNoticeSentDate != null) {
            issuable.expirationNoticeSentDate = convertDateStringToUTCDate(issuable.expirationNoticeSentDate);
          }
          if (issuable.expirationNoticeSetting !== 0) {
            issuable.expirationNoticeSetting = this.monthDiff(issuable.expirationNoticeDate, issuable.expirationDate);
          }
        });
        this.issuables = this.preserveOpenItems(this.issuables, freshIssuables, this.issuableController.primaryIdField);
        this.sortIssuables();
      } catch (err) {
        console.log('response Error! ' + err);
        clearTimeout(showLoadingSpinnerTimeout);
        self.loadingInitialData = false;
        self.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: `An error occurred retrieving ${self.issuableController.entityName}`
        });
      }
    },
    // Slightly modified from https://stackoverflow.com/questions/2536379/difference-in-months-between-two-dates-in-javascript
    monthDiff(date1: any, date2: any) {
      let months;
      months = (date2.getFullYear() - date1.getFullYear()) * 12;
      months -= date1.getMonth();
      months += date2.getMonth();
      return months <= 0 ? 0 : months;
    },
    sortIssuables() {
      const today = convertDateToUTC(new Date());
      this.issuables.sort((a,b) => {
        if (issuableExpired(a, today)){
          if (issuableExpired(b, today)) {
            return a.expirationDate.getTime() - b.expirationDate.getTime();
          }
          return 1;
        }
        if (issuableExpired(b, today)) {
          return -1;
        }
        if (issuableExpiring(a, today)){
          if (issuableExpiring(b, today)) {
            return getExpirationNoticeDate(a)!.getTime() - getExpirationNoticeDate(b)!.getTime();
          }
          return 1;
        }
        if (issuableExpiring(b, today)) {
          return -1;
        }
        return -1
      });
    },
    isIssuableExpired(issuable : any) {
      return issuableExpired(issuable, convertDateToUTC(new Date()));
    },
    closeOtherIssuables(id: any) {
      if (this.isProviderUser) {
        return;
      }
      // Collapse any editing issuables
      // Delete any existing "new" issuables
      let indexesToDelete: any = [];
      this.issuables.forEach(function (issuable, index) {
        if (issuable.isNew) {
          indexesToDelete.push(index);
        }
        if (id !== issuable.id) {
          issuable.isEditing = false;
        }
      });
      let self = this;
      indexesToDelete.forEach(function (indx: number) {
        self.issuables.splice(indx,1);
      });
    },
    addIssuable() {
      if (this.isProviderUser) {
        return;
      }
      this.closeOtherIssuables(null);

      this.issuables.unshift(
        this.issuableController.createNewModel(this.$route.params.providerId)
      );
    },
    async cancel (credential: any) {
      if (credential.isNew) {
        // Allow the editing UI to collapse before removing it from the array.
        let self = this;
        setTimeout(function () {
          self.issuables.shift();
        }, 300);
      } else {
        await this.getIssuables();
      }
    }
  }
})



// WEBPACK FOOTER //
// ./src/components/provider/cards/issuables/IssuableCard.ts